import { useState, useEffect, useCallback } from 'react';

const API = 'https://tcio-api.onrender.com';

const fmtDate = (v) => {
  if (!v) return '—';
  const s = String(v).slice(0, 10);
  const [y, m, d] = s.split('-');
  return `${parseInt(m)}/${parseInt(d)}/${y}`;
};

const TYPE_LABEL = {
  diff_admit: 'Admit date mismatch',
  diff_payer: 'Payer mismatch',
  kipu_only: 'In Kipu, NOT on bed board',
  tcio_only: 'On bed board, NOT in Kipu',
};
const TYPE_COLOR = {
  diff_admit: { bg: '#fef3c7', fg: '#92400e' },
  diff_payer: { bg: '#e0e7ff', fg: '#3730a3' },
  kipu_only: { bg: '#fee2e2', fg: '#991b1b' },
  tcio_only: { bg: '#fce7f3', fg: '#9d174d' },
};
const STATUS_LABEL = {
  open: 'OPEN', reopened: '⛔ REOPENED — claimed fixed, still present',
  resolved_pending_proof: 'Fixed — awaiting proof', proven: '✓ PROVEN',
};

export default function CensusSync({ user }) {
  const [data, setData] = useState([]);
  const [stats, setStats] = useState({});
  const [running, setRunning] = useState(false);
  const [filter, setFilter] = useState('active');
  const token = localStorage.getItem('tcio_token');

  const load = useCallback(async () => {
    const r = await fetch(`${API}/api/kipu/discrepancies`, { headers: { Authorization: `Bearer ${token}` } });
    const j = await r.json();
    if (j.ok) { setData(j.data); setStats(j.stats); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await fetch(`${API}/api/kipu/compare/run`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      await r.json();
      await load();
    } catch (e) { alert('Comparison failed'); }
    setRunning(false);
  };

  const resolve = async (row) => {
    const note = window.prompt(`Mark "${row.patient_name}" (${TYPE_LABEL[row.dtype]}) as FIXED?\n\nWhat did you do? (e.g. "corrected admit date in TCIO", "discharged in Kipu")`);
    if (note === null) return;
    await fetch(`${API}/api/kipu/discrepancies/${row.id}/resolve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ note }),
    });
    load();
  };

  const shown = data.filter(r => filter === 'all' ? true
    : filter === 'active' ? ['open', 'reopened', 'resolved_pending_proof'].includes(r.status)
    : r.status === 'proven');

  const detailText = (r) => {
    const d = r.details || {};
    if (r.dtype === 'diff_admit') return `TCIO: ${fmtDate(d.tcio_admit)} · Kipu: ${fmtDate(d.kipu_admit)}${d.kipu_location ? ` (${d.kipu_location})` : ''}`;
    if (r.dtype === 'diff_payer') return `TCIO: ${d.tcio_payer} · Kipu: ${d.kipu_payer}`;
    if (r.dtype === 'kipu_only') return `Admit ${fmtDate(d.admit)} · ${d.payer || '—'} · ${d.program || '—'} @ ${d.kipu_location || '—'}`;
    return `Admit ${fmtDate(d.admit)} · ${d.payer || '—'} · ${d.loc || '—'}`;
  };

  return (
    <div style={s.page}>
      <div style={s.headRow}>
        <div>
          <h1 style={s.h1}>Census Sync — Kipu vs TCIO</h1>
          <p style={s.sub}>Every difference between the EMR and the bed board. Fix it in whichever system is wrong, then mark it fixed — the next comparison run PROVES it (or reopens it). Humans claim; data confirms. Auto-runs daily at 6am. Kipu API covers HBTS only.</p>
        </div>
        <button onClick={runNow} disabled={running} style={s.runBtn}>{running ? 'Comparing…' : '⟳ Run Comparison Now'}</button>
      </div>

      <div style={s.statRow}>
        <div style={{ ...s.stat, borderColor: '#dc2626' }}><div style={s.statN}>{(stats.open || 0) + (stats.reopened || 0)}</div><div style={s.statL}>Open{stats.reopened ? ` (${stats.reopened} reopened)` : ''}</div></div>
        <div style={{ ...s.stat, borderColor: '#d97706' }}><div style={s.statN}>{stats.pending_proof || 0}</div><div style={s.statL}>Fixed — awaiting proof</div></div>
        <div style={{ ...s.stat, borderColor: '#16a34a' }}><div style={s.statN}>{stats.proven || 0}</div><div style={s.statL}>Proven fixed</div></div>
      </div>

      <div style={s.filters}>
        {[['active', 'Active'], ['proven', 'Proven'], ['all', 'All']].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} style={{ ...s.chip, ...(filter === k ? s.chipOn : {}) }}>{l}</button>
        ))}
      </div>

      <table style={s.table}>
        <thead><tr>{['Patient', 'MRN', 'Issue', 'Detail', 'Status', 'Seen', ''].map((h, i) => <th key={i} style={s.th}>{h}</th>)}</tr></thead>
        <tbody>
          {shown.map((r, i) => {
            const tc = TYPE_COLOR[r.dtype] || {};
            const isReopened = r.status === 'reopened';
            return (
              <tr key={r.id} style={isReopened ? s.reopenRow : (i % 2 ? s.alt : {})}>
                <td style={{ ...s.td, fontWeight: 600 }}>{r.patient_name}</td>
                <td style={s.td}>{r.mrn || '—'}</td>
                <td style={s.td}><span style={{ ...s.tag, background: tc.bg, color: tc.fg }}>{TYPE_LABEL[r.dtype]}</span></td>
                <td style={{ ...s.td, fontSize: 12 }}>{detailText(r)}</td>
                <td style={{ ...s.td, fontSize: 12 }}>
                  <div style={{ fontWeight: isReopened ? 700 : 500, color: isReopened ? '#991b1b' : r.status === 'proven' ? '#16a34a' : '#374151' }}>{STATUS_LABEL[r.status]}</div>
                  {r.resolved_by && <div style={{ fontSize: 11, color: '#6b7280' }}>{r.resolved_by} · {fmtDate(r.resolved_at)}{r.resolution_note ? ` — ${r.resolution_note}` : ''}</div>}
                </td>
                <td style={{ ...s.td, fontSize: 11, color: '#6b7280' }}>{fmtDate(r.last_seen)}</td>
                <td style={s.td}>
                  {['open', 'reopened'].includes(r.status) && <button onClick={() => resolve(r)} style={s.fixBtn}>Mark Fixed</button>}
                </td>
              </tr>
            );
          })}
          {shown.length === 0 && <tr><td colSpan={7} style={{ ...s.td, textAlign: 'center', color: '#6b7280', padding: 30 }}>Nothing here — the EMR and the board agree. 🎯</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

const s = {
  page: { padding: '24px 28px', maxWidth: 1200, margin: '0 auto' },
  headRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 },
  h1: { fontSize: 24, fontWeight: 700, color: '#1a2b45', margin: 0 },
  sub: { fontSize: 13, color: '#64748b', marginTop: 6, maxWidth: 760 },
  runBtn: { background: '#336090', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  statRow: { display: 'flex', gap: 14, margin: '18px 0' },
  stat: { flex: 1, background: '#fff', border: '1px solid #e5e7eb', borderTop: '3px solid', borderRadius: 8, padding: '14px 18px' },
  statN: { fontSize: 26, fontWeight: 700, color: '#111827' },
  statL: { fontSize: 12, color: '#6b7280', marginTop: 2 },
  filters: { display: 'flex', gap: 8, marginBottom: 12 },
  chip: { padding: '6px 14px', borderRadius: 20, border: '1px solid #d1d5db', background: '#fff', fontSize: 12, cursor: 'pointer' },
  chipOn: { background: '#336090', color: '#fff', borderColor: '#336090' },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  th: { textAlign: 'left', padding: '10px 12px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' },
  td: { padding: '10px 12px', fontSize: 13, borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' },
  alt: { background: '#fafafa' },
  reopenRow: { background: '#fee2e2', borderLeft: '3px solid #991b1b' },
  tag: { display: 'inline-block', padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 },
  fixBtn: { background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
};
